<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { useEventListener } from "@vueuse/core";
import { TooltipProvider } from "reka-ui";
import { computed, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterView, useRoute } from "vue-router";
import { useBreakpoint } from "@/design/composables/use-breakpoint";
import { useKeyboardInset } from "@/design/composables/use-keyboard-inset";
import LoadingBar from "@/design/primitives/LoadingBar.vue";
import Toast from "@/design/primitives/Toast.vue";
import DynamicForm from "@/dynamic/DynamicForm.vue";
import type { DisplayContext } from "@/dynamic/resolvers/field-widget";
import OrbeDock from "@/features/agents/components/OrbeDock.vue";
import { useSessionState } from "@/features/auth/api/session";
import PlanLimitOffer from "@/features/billing/components/PlanLimitOffer.vue";
import TrialBanner from "@/features/billing/components/TrialBanner.vue";
import { createUploadPort } from "@/features/files/api/upload-port";
import { useQueueStream } from "@/features/inbox/api/use-queue-alert";
import PlatformNoticeBanner from "@/features/platform/components/PlatformNoticeBanner.vue";
import SupportSessionBanner from "@/features/platform/components/SupportSessionBanner.vue";
import {
	createAutofillPort,
	createLookupPort,
	createSubformPort,
} from "@/features/records/api/lookup-port";
import { createOptionSourcePort } from "@/features/records/api/option-source-port";
import OnboardingJourney from "@/pages/OnboardingJourney.vue";
import { useMyAccess } from "@/shared/access/use-access";
import { useStorageLimit } from "@/shared/billing/storage-limit";
import { DESTINATIONS } from "@/shared/contracts/destinations";
import {
	AUTOFILL_PORT,
	DISPLAY_CONTEXT,
	LOOKUP_PORT,
	OPEN_BEN,
	OPTION_SOURCE_PORT,
	RECORD_FORM,
	SUBFORM_PORT,
	UPLOAD_PORT,
} from "@/shared/contracts/ports";
import { tenantContext } from "@/shared/format";
import { loadingState } from "@/shared/http/loading-bar";
import AppBoot from "./layout/AppBoot.vue";
import AppHeader from "./layout/AppHeader.vue";
import AppNav from "./layout/AppNav.vue";
import AppRail from "./layout/AppRail.vue";
import CommandPalette from "./layout/CommandPalette.vue";
import { shellMessages } from "./layout/i18n/shell";
import { useDestinations } from "./layout/use-destinations";
import { useTrialWall } from "./layout/use-trial-wall";
import WorkspaceChip from "./layout/WorkspaceChip.vue";
import { usePanelLocale } from "./use-panel-locale";

/**
 * Application shell.
 *
 * Now a shell in the real sense: it renders the chrome and a `<RouterView>`, and
 * knows nothing about which page is showing. Everything below the header
 * survives navigation — which is what lets Ben's conversation continue while you
 * move around, instead of resetting on every click.
 *
 * The nav is a permanent rail from 768px up and a drawer below it — 224px of
 * sidebar on a 360px screen leaves no room for what it points at.
 */
const { identity } = useSessionState();
const { isMobile } = useBreakpoint();
/** O teclado do telefone medido uma vez, para quem vive no fundo da tela. */
useKeyboardInset();
const route = useRoute();

const paletteOpen = ref(false);
const navOpen = ref(false);

const benPresentation = ref({ open: false, wide: false });
function toggleBenChat(): void {
	dock.value?.toggleChat();
}
const dock = ref<{
	toggle: () => void;
	toggleChat: () => void;
	openWith: (code?: string, prefill?: string, fresh?: boolean, send?: boolean) => void;
} | null>(null);

provide(OPEN_BEN, (input = {}) => {
	dock.value?.openWith(input.agentCode, input.prefill, input.newSession, input.send);
});

/**
 * Para onde dá para ir, provido uma vez para quem precisar (`DESTINATIONS`).
 *
 * A moldura é a única camada que pode ver o catálogo de telas e as entidades do
 * tenant ao mesmo tempo — um feature não lê outro feature (SPEC §6). É daqui que
 * o assistente descobre os destinos, sem uma segunda lista escrita à mão.
 */
provide(
	DESTINATIONS,
	useDestinations(() => identity.value !== null && route.meta.bare !== true),
);

/**
 * Pages that render ALONE: the public form, the webchat, sign-in, sign-up and
 * invite acceptance. Marked on the route rather than matched by path here, so
 * adding one does not mean remembering to edit this condition.
 */
const bare = computed(() => route.meta.bare === true);

provide(UPLOAD_PORT, createUploadPort());
provide(RECORD_FORM, DynamicForm);
provide(LOOKUP_PORT, createLookupPort());
provide(SUBFORM_PORT, createSubformPort());
// As opções que saem de uma consulta do tenant (§3.11). Aqui e não no formulário
// público pela mesma razão do auto-preenchimento: rodar um artefato é trabalho do
// banco do tenant, e quem ainda não entrou não pede isso.
provide(OPTION_SOURCE_PORT, createOptionSourcePort());
// Só no painel, e de propósito: o formulário público não provê esta porta porque
// lá não existe "quem responde" — e um estranho não recebe de volta o telefone
// de um contato que ele escolheu numa busca (§2.6 #16).
provide(AUTOFILL_PORT, createAutofillPort(useQueryClient()));

/**
 * Quem é lido, vê.
 *
 * Uma consulta ao lado da do workspace, para todo mundo e não só para quem
 * administra a instalação: enquanto houver uma sessão de suporte aberta sobre
 * este workspace, quem trabalha nele tem de saber — é a metade do desenho que a
 * senha mestra da V2 nunca teve. O componente se desenha sozinho como nada
 * quando não há nenhuma.
 */
const signedIn = computed(() => Boolean(identity.value) && !bare.value);
// Só com sessão e fora das rotas `bare`: ver o comentário em `useMyAccess`.
const myAccess = useMyAccess(() => signedIn.value);
const orbVisible = computed(() => signedIn.value && myAccess.data.value?.benPanel !== false);
watch(orbVisible, (visible) => {
	if (!visible) benPresentation.value = { open: false, wide: false };
});

/**
 * O aviso de conversa nova, ouvido pelo SHELL — que é a única coisa que não desmonta.
 * Uma conexão por aba: sobrevive à navegação, não duplica o bipe com a inbox aberta, e
 * sem sessão não abre (a preferência sobrevive ao logout, o token não).
 */
useQueueStream(signedIn);

/**
 * O idioma do PAINEL, resolvido em cascata: esta pessoa → este workspace → este
 * navegador → inglês. E o que o WORKSPACE é, publicado para quem formata número
 * fora de um componente. As duas coisas são vizinhas e não a mesma: um operador
 * com o painel em inglês NÃO faz o cliente dele receber inglês.
 */
usePanelLocale(signedIn);

/**
 * A parede do teste vencido. Aqui e não numa tela porque ela vale para TODAS: é
 * a moldura que sabe em que rota se está e é a única que não desmonta.
 */
useTrialWall(() => signedIn.value);

/**
 * O armazenamento cheio, dito UMA vez para todas as portas de upload: elas anotam a
 * recusa em `shared/billing/storage-limit`, e só a moldura pode ler a cobrança.
 */
const { storageLimit, dismissStorageLimit } = useStorageLimit();

/** A moldura diz "restaurando sessão" antes de qualquer feature montar, então ela
 * também precisa de escopo — o mesmo dicionário do cabeçalho e do menu. */
const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: shellMessages });

/**
 * O contexto de exibição do TENANT — moeda, fuso e o idioma do workspace, nunca
 * os do navegador. A tela de status da fase 1 mostrava um evento das 15:45 como
 * 12:45, que é o mesmo defeito que faz um relatório apontar o dia errado.
 *
 * Os três valores agora saem de `shared/format`, que é quem `usePanelLocale`
 * acabou de abastecer a partir da conta — onde as colunas moram desde a primeira
 * migração. O fallback continua existindo lá dentro, e não é preguiça: isto roda
 * antes da consulta responder e em toda tela enquanto ela está no ar. Duas cópias
 * do fallback era como uma tela mostrava BRL depois de a outra já saber que era EUR.
 */
const display = computed<DisplayContext>(() => ({
	...tenantContext(),
	...(identity.value ? { viewer: identity.value.userId } : {}),
}));
provide(DISPLAY_CONTEXT, display);

// Navigating closes the drawer: leaving it open over the page someone just chose
// is the classic mobile-menu annoyance.
watch(route, () => {
	navOpen.value = false;
});

/**
 * Quem está escrevendo continua escrevendo.
 *
 * O ⌘K nunca checou isto e passou — um atalho com modificador raramente colide
 * com o que se digita. O J não tem essa sorte: em `Ctrl+J` num campo de texto o
 * navegador já faz coisas, e roubar a tecla de quem está no meio de uma resposta
 * ao cliente seria pior do que não ter atalho nenhum.
 */
function typing(target: EventTarget | null): boolean {
	const el = target as HTMLElement | null;
	if (!el?.tagName) return false;
	return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable === true;
}

useEventListener(window, "keydown", (event: KeyboardEvent) => {
	if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
		event.preventDefault();
		paletteOpen.value = !paletteOpen.value;
	}
	// ⌘J abre e fecha o Ben, e retoma a última conversa em vez de começar do zero.
	// Só quando há doca: quem escondeu o Ben não teve a tecla de downloads do
	// navegador tomada em troca de nada.
	if (
		(event.metaKey || event.ctrlKey) &&
		event.key.toLowerCase() === "j" &&
		!typing(event.target) &&
		dock.value
	) {
		event.preventDefault();
		void toggleBenChat();
	}
	if (event.key === "Escape") navOpen.value = false;
});
</script>

<template>
	<!--
		O provedor dos tooltips. Envolve TUDO, e não é sibling do `<Toast />` por um
		motivo mecânico: um `TooltipRoot` sem este contexto acima dele lança, e as
		rotas `meta.bare` — formulário público, chat público, documento público,
		entrar — também desenham controles com explicação. Ele não emite DOM: são
		duas linhas de contexto, no mesmo lugar e pela mesma razão que o toast.
		A espera de 300ms é a diferença mais visível para o `title=` nativo, que
		leva cerca de um segundo e meio e nunca aparece pelo teclado.
	-->
	<TooltipProvider :delay-duration="300" :skip-delay-duration="200">
		<!-- Above the `bare` branch on purpose: a public form that fails to send owes
		     the same answer a signed-in screen does. -->
		<Toast :dismiss-label="t('toast.dismiss')" />
		<!-- A barra do topo (a da V2): azul com trabalho em voo, vermelha quando o
		     último falhou. Também acima do ramo `bare` — a página pública carrega e
		     falha como qualquer outra. O estado vem de `shared/http/loading-bar`. -->
		<LoadingBar :state="loadingState" />

		<!-- Alone: no nav, no header, nothing that assumes a session. -->
		<RouterView v-if="bare" />

		<!-- Entre abrir e estar dentro: a marca, e só depois de 300 ms — ver `AppBoot`.
		     SEM SESSÃO a casca não monta, restaurando ou não: o menu, o cabeçalho e a
		     tela por baixo disparavam ~15 consultas do workspace que voltavam 401
		     antes de a guarda mandar a pessoa para o login. -->
		<AppBoot v-else-if="!identity" />

		<!--
			`padding-bottom: var(--kb-inset)`: a casca é `h-dvh` e o `dvh` do iPhone
			não sabe do teclado — quem vive no fundo dela (o compositor da caixa de
			entrada, a coluna do Ben dentro dele) ficava debaixo do teclado. Onde a
			janela já encolhe sozinha a variável vale zero. Ver `use-keyboard-inset`.
		-->
		<div
			v-else
			class="flex h-dvh flex-col overflow-hidden"
			style="padding-bottom: var(--kb-inset, 0px)"
		>
			<AppHeader
				:class="benPresentation.open ? 'invisible' : ''"
				:inert="benPresentation.open"
				:signed-in="signedIn"
				:ben-available="orbVisible"
				@open-nav="navOpen = true"
				@open-palette="paletteOpen = true"
			/>

			<SupportSessionBanner :enabled="signedIn" />
			<!-- A faixa de aviso da plataforma — irmã da de suporte, mesmo lugar. -->
			<PlatformNoticeBanner :enabled="signedIn" />
			<!-- The trial band (§10.2): draws nothing unless the subscription says trial. -->
			<TrialBanner :enabled="signedIn" />
			<OnboardingJourney v-if="signedIn && (route.path.startsWith('/comecar') || route.query.onboarding === '1')" />

			<div class="flex min-h-0 flex-1" :inert="benPresentation.open" :class="benPresentation.open && benPresentation.wide ? 'invisible' : ''">
				<AppRail v-if="!isMobile" />

				<div
					v-if="isMobile && navOpen"
					class="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
					@click="navOpen = false"
				/>
				<aside
					v-if="isMobile"
					class="fixed inset-y-2 left-2 z-50 flex w-[min(18rem,calc(100vw-3rem))] flex-col overflow-hidden rounded-2xl bg-nav-bg shadow-lg transition-transform duration-200 [&_a]:h-11 [&_nav_button]:min-h-11"
					:class="navOpen ? 'translate-x-0' : '-translate-x-[calc(100%+1rem)]'"
					:aria-hidden="!navOpen"
				>
					<!-- O seletor de unidade, como no topo do rail: sem ele, no celular não havia
					     por onde trocar de workspace. -->
					<div class="flex h-14 shrink-0 items-center px-3">
						<WorkspaceChip class="min-w-0 flex-1" />
					</div>
					<AppNav drawer @navigate="navOpen = false" />
				</aside>

				<RouterView />
			</div>

			<CommandPalette :open="paletteOpen" @close="paletteOpen = false" />

			<PlanLimitOffer
				v-if="storageLimit"
				variant="dialog"
				resource="storage_gb"
				:used="storageLimit.used"
				:limit="storageLimit.limit"
				@update:open="dismissStorageLimit()"
			/>

			<!-- Ben. Outside the RouterView on purpose: an open conversation has to
			     survive changing page, or anything with more than one step means
			     explaining the business again. Escondê-lo é uma preferência deste
			     navegador, e a volta está no menu do usuário — nunca dentro da doca. -->
			<OrbeDock v-if="orbVisible" ref="dock" topbar @presentation="benPresentation = $event" :teach="route.path.startsWith('/comecar') || route.query.onboarding === '1'" />
		</div>
	</TooltipProvider>
</template>
